import React, { FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { FooterNavigationBar } from '@component/FooterNavigationBar';
import { useMediaDesktop, useMediaMobile } from '@app/react/common/useMedia';
import { useOrderPathParams } from '@app/react/contexts/useOrderPathParams';
import { useHistoryGoParts } from '@app/react/contexts/useHistoryGoParts';
import { validateCart } from '@app/react/common/validateCart';
import { getCsrfToken } from '@app/react/common/getCsrfToken';
import {
    addCartAction,
    yourDesignAction,
    showConfirm3DAction,
} from '@app/react/action';
import type { DesignSet } from '@app/react/const/type';
import type { AppState } from '@app/react/reducer';

export const FooterNavigationBarContainer: FC<{ enablePreview?: boolean }> = ({
    enablePreview = false,
    isCustomView = false,
}) => {
    const dispatch = useDispatch();
    const goParts = useHistoryGoParts();
    const isDesktop = useMediaDesktop();
    const isMobile = useMediaMobile();
    const { arrowType, orderType } = useOrderPathParams();
    const customizeData = useSelector<AppState, DesignSet>(
        (state) => state.customize,
    );
    const isAddCart = useMemo(
        () => validateCart(arrowType, orderType, customizeData),
        [customizeData, arrowType, orderType],
    );

    const onAddCart = useCallback(() => {
        const csrf = getCsrfToken();
        const act = addCartAction.started({
            csrf,
            arrowType,
            orderType,
            parts: customizeData,
        });
        dispatch(act);
    }, [dispatch, customizeData, arrowType, orderType]);

    const onPreview3d = useCallback(() => {
        dispatch(showConfirm3DAction({ visible: true }));
        goParts('preview');
    }, [goParts, dispatch]);

    const onHomeClickDesktop = useCallback(() => {
        const {
            ...data
        } = customizeData;
        dispatch(
            yourDesignAction.started({
                arrowType,
                orderType,
                params: { ...data },
            }),
        );
        goParts();
    }, [dispatch, goParts, customizeData, arrowType, orderType]);

    const onHomeClickMobile = useCallback(() => {
        const {
            ...data
        } = customizeData;
        batch(() => {
            dispatch(
                yourDesignAction.started({
                    arrowType,
                    orderType,
                    params: { ...data },
                }),
            );
            dispatch(showConfirm3DAction({ visible: false }));
        });
        goParts();
    }, [dispatch, goParts, customizeData, arrowType, orderType]);

    return (
        <FooterNavigationBar
            onAddCart={onAddCart}
            onPreview3d={onPreview3d}
            onHomeClick={isDesktop ? onHomeClickDesktop : onHomeClickMobile}
            isAddCart={isAddCart}
            isCustomView={isCustomView}
            is3dView={enablePreview}
            isMobile={isMobile}
        />
    );
};
